import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { AppShell } from './AppShell.tsx'
import { AuditProvider } from '../audit/AuditProvider.tsx'
import { LoadingState } from '../components/LoadingState.tsx'

// Real URL routing with lazy feature boundaries. There is no global conditional
// view switch: each section is its own route and its own chunk.
const HomePage = lazy(() => import('../features/home/HomePage.tsx'))
const KeycapTrayPage = lazy(() => import('../features/keycap-tray/KeycapTrayPage.tsx'))
const SwitchTrayPage = lazy(() => import('../features/switch-tray/SwitchTrayPage.tsx'))
const GridfinityPage = lazy(() => import('../features/gridfinity/GridfinityPage.tsx'))
const ToolTrayPage = lazy(() => import('../features/tool-tray/ToolTrayPage.tsx'))
const CabinetPage = lazy(() => import('../features/cabinet/CabinetPage.tsx'))
const ProjectsPage = lazy(() => import('../features/keycap-projects/ProjectsPage.tsx'))
const ProjectPage = lazy(() => import('../features/keycap-projects/ProjectPage.tsx'))
const ShaperDesignerPage = lazy(() => import('../features/shaper-designer/ShaperDesignerPage.tsx'))
const BambuDesignerPage = lazy(() => import('../features/bambu-designer/BambuDesignerPage.tsx'))
const PlaygroundPage = lazy(() => import('../features/playground/PlaygroundPage.tsx'))
const FilamentsPage = lazy(() => import('../features/filaments/FilamentsPage.tsx'))
const OrdersPage = lazy(() => import('../features/filaments/OrdersPage.tsx'))
const MaintenancePage = lazy(() => import('../features/maintenance/MaintenancePage.tsx'))
const LibraryPage = lazy(() => import('../features/library/LibraryPage.tsx'))
const LibraryModelPage = lazy(() => import('../features/library/ModelPage.tsx'))
const LibraryInboxPage = lazy(() => import('../features/library/InboxPage.tsx'))
const LibraryReviewPage = lazy(() => import('../features/library/ReviewPage.tsx'))
const LibraryOrganizePage = lazy(() => import('../features/library/OrganizePage.tsx'))
const LibraryManagePage = lazy(() => import('../features/library/ManagePage.tsx'))
const SettingsPage = lazy(() => import('../features/settings/SettingsPage.tsx'))
const AdminPage = lazy(() => import('../features/admin/AdminPage.tsx'))

function NotFound() {
  return (
    <Stack spacing={2} sx={{ alignItems: 'flex-start', p: 2 }}>
      <Typography variant="h1" component="h1">Page not found</Typography>
      <Typography color="text.secondary">
        That address does not match anything in ShapePilot.
      </Typography>
      <Button component={Link} to="/" variant="contained">
        Go to the workshop
      </Button>
    </Stack>
  )
}

export function AppRoutes() {
  return (
    <AuditProvider>
      <Routes>
        <Route element={<AppShell />}>
          <Route
            index
            element={(
              <Suspense fallback={<LoadingState label="Opening the workshop…" />}>
                <HomePage />
              </Suspense>
            )}
          />
          <Route
            path="/keycap-tray"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <KeycapTrayPage />
                </Suspense>
              </Box>
            }
          />
          {/* The designer addresses one saved tray, so a project can link
              straight to it and the back button works. The bare path is still
              the scratch designer. */}
          <Route
            path="/keycap-tray/:designId"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <KeycapTrayPage />
                </Suspense>
              </Box>
            }
          />
          {/* Same addressing as the keycap tray: the bare path is a scratch
              tray, and `/:designId` names a saved one so a link, a reload and
              the back button all land on the same design. */}
          <Route
            path="/switch-tray"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <SwitchTrayPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/switch-tray/:designId"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <SwitchTrayPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/gridfinity"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <GridfinityPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/gridfinity/:designId"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <GridfinityPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/tool-tray"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <ToolTrayPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/tool-tray/:designId"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <ToolTrayPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/cabinet"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the designer…" />}>
                  <CabinetPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/projects"
            element={
              <Suspense fallback={<LoadingState label="Loading your projects…" />}>
                <ProjectsPage />
              </Suspense>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <Suspense fallback={<LoadingState label="Loading the project…" />}>
                <ProjectPage />
              </Suspense>
            }
          />
          <Route
            path="/shaper-designer"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the Shaper designer…" />}>
                  <ShaperDesignerPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/bambu-designer"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the Bambu designer…" />}>
                  <BambuDesignerPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/playground"
            element={
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Suspense fallback={<LoadingState label="Loading the playground…" />}>
                  <PlaygroundPage />
                </Suspense>
              </Box>
            }
          />
          <Route
            path="/filaments"
            element={
              <Suspense fallback={<LoadingState label="Loading your filaments…" />}>
                <FilamentsPage />
              </Suspense>
            }
          />
          <Route
            path="/filaments/orders"
            element={
              <Suspense fallback={<LoadingState label="Loading your orders…" />}>
                <OrdersPage />
              </Suspense>
            }
          />
          <Route
            path="/maintenance"
            element={
              <Suspense fallback={<LoadingState label="Loading your maintenance log…" />}>
                <MaintenancePage />
              </Suspense>
            }
          />
          {/* The fixed sub-pages are declared before `/library/:modelId`, so
              a model id can never shadow them. */}
          <Route
            path="/library"
            element={(
              <Suspense fallback={<LoadingState label="Loading the library…" />}>
                <LibraryPage />
              </Suspense>
            )}
          />
          <Route
            path="/library/inbox"
            element={(
              <Suspense fallback={<LoadingState label="Loading the inbox…" />}>
                <LibraryInboxPage />
              </Suspense>
            )}
          />
          <Route
            path="/library/review"
            element={(
              <Suspense fallback={<LoadingState label="Loading…" />}>
                <LibraryReviewPage />
              </Suspense>
            )}
          />
          <Route
            path="/library/organize"
            element={(
              <Suspense fallback={<LoadingState label="Loading the plan…" />}>
                <LibraryOrganizePage />
              </Suspense>
            )}
          />
          <Route
            path="/library/manage"
            element={(
              <Suspense fallback={<LoadingState label="Loading…" />}>
                <LibraryManagePage />
              </Suspense>
            )}
          />
          <Route
            path="/library/:modelId"
            element={(
              <Suspense fallback={<LoadingState label="Loading the model…" />}>
                <LibraryModelPage />
              </Suspense>
            )}
          />
          <Route
            path="/settings"
            element={
              <Suspense fallback={<LoadingState label="Loading settings…" />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<LoadingState label="Loading admin…" />}>
                <AdminPage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </AuditProvider>
  )
}
